import { Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { ChannelMappingOptions } from 'api/integrations/slack/slack.types';
import { Checkbox } from 'components/atoms/checkbox';
import { Option } from 'components/atoms/select';
import { useTranslate } from 'hooks/use-translation';
import { SectionLayout } from '../common';
import { UserSelectionModal } from './user-selection-modal';
import {
  Container,
  ModalTriggerButton,
  SubText,
  Text,
} from './user-selection.styles';
import {
  UserSelectionOptions,
  UserSelectionTypes,
} from './user-selection.types';
import { ValueRenderer } from './value-renderer';

export const UserSelection = ({
  settings,
  usersList,
  handleChange,
  loading,
}: UserSelectionTypes) => {
  const translate = useTranslate();
  const [triggerVisible, setTriggerVisible] = useState(
    settings?.addSpecificUsersToChannel,
  );
  const [modalVisibility, setModalVisibility] = useState(false);

  const handleCancel = () => {
    setModalVisibility(false);
  };

  const handleChecked = (
    option: UserSelectionOptions,
    e: CheckboxChangeEvent,
  ) => {
    switch (option) {
      case UserSelectionOptions.SelectedMembers: {
        if (isEmpty(settings?.selectedUsers) && e.target.checked) {
          setModalVisibility(true);
        } else {
          // when there are no selected users,
          setTriggerVisible(e.target.checked);
          if (settings) {
            handleChange?.({
              ...settings,
              addSpecificUsersToChannel: e.target.checked,
            });
          }
        }

        break;
      }

      case UserSelectionOptions.AllMembers: {
        if (settings) {
          handleChange?.({
            ...settings,
            addAllProjectMembersToChannel: e.target.checked,
          });
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  const handleUserSelection = (selectedUsers?: Option[]) => {
    handleCancel();
    if (settings) {
      setTriggerVisible(true);
      handleChange?.({
        ...settings,
        addSpecificUsersToChannel: true,
        selectedUsers,
      });
    }
  };

  return (
    <SectionLayout
      title={translate('settings.integrations.slack.member_selection.title')}
    >
      <Space align="start" direction="vertical">
        <div>
          <Checkbox
            onChange={(e) => handleChecked(UserSelectionOptions.AllMembers, e)}
            disabled={
              settings?.channelMappingMechanism !==
              ChannelMappingOptions.FixedChannel
            }
            defaultChecked={settings?.addAllProjectMembersToChannel}
            data-cy="integrations.slack.every_members_in_project_checkbox"
          />
          <Container>
            <Text>
              {translate(
                'settings.integrations.slack.member_selection.all.text',
              )}
            </Text>
            <SubText>
              {translate(
                'settings.integrations.slack.member_selection.all.sub_text',
              )}
            </SubText>
          </Container>
        </div>
        <div>
          <Checkbox
            onChange={(e) =>
              handleChecked(UserSelectionOptions.SelectedMembers, e)
            }
            disabled={settings == null}
            defaultChecked={settings?.addSpecificUsersToChannel}
            data-cy="integrations.slack.default_members_checkbox"
          />
          <Container>
            <Text>
              {translate(
                'settings.integrations.slack.member_selection.selected.text',
              )}
            </Text>

            {triggerVisible && settings?.selectedUsers?.length ? (
              <ValueRenderer userList={settings?.selectedUsers} />
            ) : null}

            {triggerVisible ? (
              <ModalTriggerButton
                type="link"
                onClick={() => setModalVisibility(true)}
              >
                {translate('choose')}
              </ModalTriggerButton>
            ) : null}
          </Container>
        </div>
      </Space>

      {modalVisibility ? (
        <UserSelectionModal
          onSelect={handleUserSelection}
          onClose={handleCancel}
          value={settings?.selectedUsers}
          usersList={usersList}
          loading={loading}
        />
      ) : null}
    </SectionLayout>
  );
};
